import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X } from 'lucide-react';

const SearchAutocomplete = ({
  placeholder,
  value,
  options = [],
  onSelect,
  className = "",
  inputClassName = "",
  dropdownClassName = "",
  disabled = false
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Sync internal query text when value prop changes externally
  useEffect(() => {
    if (value === '' || value === null || value === undefined) {
      setQuery('');
      return;
    }
    const matchedOption = options.find(opt => {
      if (typeof opt === 'object' && opt !== null) {
        return String(opt.id) === String(value) || opt.name.toLowerCase() === String(value).toLowerCase();
      }
      return String(opt).toLowerCase() === String(value).toLowerCase();
    });

    if (matchedOption) {
      setQuery(typeof matchedOption === 'object' ? matchedOption.name : String(matchedOption));
    } else {
      setQuery(String(value));
    }
  }, [value, options]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on query
  const filteredOptions = options.filter(opt => {
    const label = typeof opt === 'object' ? opt.name : String(opt);
    return label.toLowerCase().includes(query.toLowerCase());
  });

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setIsOpen(true);
    
    // Check if typed text matches an option exactly or pass text
    const exactMatch = options.find(opt => {
      const label = typeof opt === 'object' ? opt.name : String(opt);
      return label.toLowerCase() === val.trim().toLowerCase();
    });

    if (exactMatch) {
      const matchVal = typeof exactMatch === 'object' ? exactMatch.id : String(exactMatch);
      const matchLabel = typeof exactMatch === 'object' ? exactMatch.name : String(exactMatch);
      if (onSelect) onSelect(matchVal, matchLabel, exactMatch);
    } else {
      if (onSelect) onSelect(val, val, null);
    }
  };

  const handleSelectOption = (opt) => {
    const label = typeof opt === 'object' ? opt.name : String(opt);
    const val = typeof opt === 'object' ? opt.id : String(opt);
    setQuery(label);
    setIsOpen(false);
    if (onSelect) {
      onSelect(val, label, opt);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setQuery('');
    setIsOpen(false);
    if (onSelect) {
      onSelect('', '', null);
    }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative flex items-center w-full">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`truncate ${inputClassName}`}
        />
        {query ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-1 sm:right-3 text-gray-400 hover:text-gray-600 p-0.5 rounded-full cursor-pointer focus:outline-none flex-shrink-0"
          >
            <X size={12} className="sm:w-3.5 sm:h-3.5" />
          </button>
        ) : (
          <ChevronDown
            size={12}
            className={`absolute right-1 sm:right-3 text-gray-400 pointer-events-none transition-transform duration-200 sm:w-3.5 sm:h-3.5 ${isOpen ? 'rotate-180 text-[#2ab391]' : ''}`}
          />
        )}
      </div>

      {isOpen && !disabled && (
        <div className={`absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto z-[9999] py-1 text-left ${dropdownClassName}`}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, idx) => {
              const label = typeof opt === 'object' ? opt.name : String(opt);
              const val = typeof opt === 'object' ? opt.id : String(opt);
              const isSelected = String(value).toLowerCase() === String(val).toLowerCase() || String(value).toLowerCase() === label.toLowerCase();
              return (
                <div
                  key={idx}
                  onClick={() => handleSelectOption(opt)}
                  className={`px-3.5 py-2 text-xs sm:text-sm font-medium cursor-pointer transition-colors flex items-center justify-between ${
                    isSelected ? 'bg-[#eaf7f4] text-[#2ab391] font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-[#2ab391]'
                  }`}
                >
                  <span>{label}</span>
                </div>
              );
            })
          ) : (
            <div className="px-4 py-3 text-xs text-gray-400 italic text-center">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
